import { useState } from 'react';
import { Link } from 'react-router-dom';
import { partnerErrorMessage } from '../state/partnerError';
import { useClientSession } from '../state/useClientSession';
import { PARTNER_ROLE_LABELS, type PartnerRole } from '../types';

/**
 * The permanent "who am I working for" bar. While a partner user is inside a client company it stays at the top of every page, names the client, the firm and the person's role, and always offers Exit Client.
 * It also says, in words, when the session cannot be trusted to work: another window changed the client, the access ended, or the 10-minute token ran out. It is a label, not a control: every request is
 * authorized by the server regardless of what this bar shows.
 */
export function ClientWorkingBanner() {
  const { status, delegation, notice, exit, renew, adoptCurrentClient: adopt } = useClientSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === 'none' || !delegation) return null;

  const role = PARTNER_ROLE_LABELS[delegation.partnerRole as PartnerRole] ?? delegation.partnerRole;
  const ends = new Date(delegation.expiresAtUtc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(partnerErrorMessage(e, 'That did not work.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`pp-client-bar pp-client-bar--${status}`} role="region" aria-label="Working in a client company">
      <div className="pp-client-bar-main">
        <p className="pp-client-bar-title">
          <span className="pp-client-bar-label">Working in</span> <strong>{delegation.clientName}</strong>
          <span className="pp-client-bar-sub"> for {delegation.partnerFirmName} as {role}</span>
        </p>

        {status === 'active' && (
          <p className="pp-client-bar-sub" role="status">
            Your access renews automatically while you work (this session ends {ends}). <Link to={`/partner/clients/${delegation.relationshipId}/workspace`}>Open workspace</Link>
          </p>
        )}
        {status === 'expired' && (
          <p role="alert">Your session in this client ended. Continue to start another; every check runs again.</p>
        )}
        {status === 'ended' && <p role="alert">Your access to this client has ended. Nothing more can be done here.</p>}
        {status === 'otherWindow' && (
          <p role="alert">
            Another window opened a different client, so this window can no longer work in the one it had. The browser is now in <strong>{delegation.clientName}</strong>.
          </p>
        )}
        {(notice || error) && <p role="alert">{error ?? notice}</p>}
      </div>

      <div className="pp-client-bar-actions">
        {status === 'expired' && (
          <button type="button" className="pp-button pp-button--primary" disabled={busy} onClick={() => void run(renew)}>
            Continue working
          </button>
        )}
        {status === 'otherWindow' && (
          <button type="button" className="pp-button" disabled={busy} onClick={adopt}>
            Work in {delegation.clientName} here
          </button>
        )}
        <button type="button" className="pp-button pp-button--danger" disabled={busy} onClick={() => void run(exit)}>
          Exit Client
        </button>
      </div>
    </div>
  );
}
